import React, { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import TextInputComp from "../customer/TextInputComp";

interface ProductSelectorDropdownProps {
  open: boolean;
  products: any[];
  onSelect: (product: any) => void;
  onClose: () => void;
  placeholder?: string;
}

const ProductSelectorDropdown: React.FC<ProductSelectorDropdownProps> = ({
  open,
  products,
  onSelect,
  onClose,
  placeholder = "Search product...",
}) => {
  const [search, setSearch] = useState("");

  const filteredProducts = useMemo(() => {
    return products.filter((item) =>
      (item.name || item.productName || "")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [products, search]);

  if (!open) return null;

  return (
    <div className="mt-5 w-full rounded-2xl border border-gray-200 bg-white shadow-md overflow-hidden text-left animate-slide-up">
      {/* SEARCH AND CLOSE PANEL ROW */}
      <div className="flex items-center justify-between px-4 pt-4 gap-4">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10"
          />
          <TextInputComp
            placeholder={placeholder}
            value={search}
            onChangeText={setSearch}
            className="w-full pl-10 h-10 rounded-xl bg-slate-50 border-slate-200"
          />
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 text-gray-400 hover:bg-slate-50 rounded-xl border border-slate-200 shrink-0 cursor-pointer"
        >
          <X size={16} />
        </button>
      </div>

      {/* GRID PRODUCT SELECTION TILES */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-4 max-h-[360px] overflow-y-auto">
        {filteredProducts.length === 0 ? (
          <div className="col-span-full py-12 text-center text-xs font-bold text-gray-400 italic">
            No products match your search.
          </div>
        ) : (
          filteredProducts.map((item) => {
            const productName = item.name || item.productName || "Unnamed Item";
            const productPrice =
              typeof item.price === "number"
                ? `AED ${item.price.toFixed(2)}`
                : item.price;
            const stockCount = typeof item.stock === "number" ? item.stock : 0;
            const isOutOfStock = stockCount <= 0;

            return (
              <div
                key={item.id || item.productId}
                onClick={() => {
                  onSelect(item);
                  setSearch("");
                }}
                className="relative border border-slate-200 rounded-xl p-3 cursor-pointer hover:border-mainBrandColor hover:shadow-2xs transition group flex flex-col justify-between bg-white"
              >
                {/* IMAGE CONTAINER WITH STOCK BADGE */}
                <div className="relative w-full h-28 bg-slate-50 rounded-lg flex items-center justify-center p-2 overflow-hidden shrink-0">
                  {/* STOCK STATUS BADGE */}
                  <span
                    className={`absolute top-1.5 right-1.5 z-10 text-[10px] font-bold px-2 py-0.5 rounded-full border shadow-xs ${
                      isOutOfStock
                        ? "bg-rose-50 text-rose-600 border-rose-200"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200"
                    }`}
                  >
                    {isOutOfStock ? "Sold Out" : `Stock: ${stockCount}`}
                  </span>

                  {item.thumbnail ? (
                    <img
                      src={item.thumbnail}
                      alt={productName}
                      className="max-w-full max-h-full object-contain group-hover:scale-105 transition duration-200"
                    />
                  ) : (
                    <div className="text-[10px] font-bold text-gray-300">
                      No Image
                    </div>
                  )}
                </div>

                {/* DETAILS */}
                <div className="mt-2.5 space-y-1 flex-1 flex flex-col justify-between">
                  <h3 className="text-xs font-bold text-slate-800 line-clamp-2 min-h-[32px]">
                    {productName}
                  </h3>
                  <div>
                    {item.brandName && (
                      <p className="text-[10px] text-gray-400 font-semibold truncate">
                        {item.brandName}
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-sm font-black text-mainBrandColor">
                        {productPrice}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ProductSelectorDropdown;